import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MarkdownRenderer from '../components/MicroLearning/MarkdownRenderer';
import TranslateAmharicButton from '../components/MicroLearning/TranslateAmharicButton';
import AIHelpPanel from '../components/MicroLearning/AIHelpPanel';
import PastESSLCEQuiz from '../components/MicroLearning/PastESSLCEQuiz';
import ContentFigure from '../components/MicroLearning/ContentFigure';
import ComponentTriggers from '../components/MicroLearning/ComponentTriggers';
import PackageMocks from '../components/Package/PackageMocks';
import PackageSitting from '../components/Package/PackageSitting';
import PackageBar from '../components/Package/PackageBar';
import MicroLearningModule from '../components/MicroLearning/MicroLearningModule';
import { diagnosticAPI, packagesAPI, pastExamAPI, track } from '../services/api';
import './PackageShell.css';

/**
 * Three delivery shells for one package, side by side so they can be compared
 * on the same content.
 *
 *   /p/:pkg/a/:slug   reference   — outline + one long scroll, checks inline
 *   /p/:pkg/b/:slug   timed room  — framed as a repair worth N marks
 *   /p/:pkg/c/:slug   daily brief — one phase at a time, one sitting
 *
 * The shells differ in LAYOUT ONLY. All three receive the identical payload
 * from GET /student/packages/{pkg}/lessons/{slug}, so a shell can never become
 * a content fork — if one of them needs different content, that is a signal the
 * content model is wrong, not that the shell needs its own endpoint.
 *
 * ## Two bodies, cased on a declared kind
 *
 * Quantitative and verbal want different body layouts and forcing one on both
 * produces a page that serves neither. Quant is derivational — prose, tables, a
 * worked example, a few long drills taken one at a time. Verbal is exposure —
 * short sentence-items, a pattern bank, many fast reps where seeing the next
 * item immediately IS the practice.
 *
 * So `renderDrills` cases on `lesson.kind`, which the CONTENT declares. It never
 * cases on the subject name: that branch would have to grow an arm for every
 * future package, whereas a new package either picks an existing kind or
 * declares a new one without touching this file.
 */

type Lesson = {
  module_id: string;
  title: string;
  topic: string;
  subtopic: string;
  minutes: number;
  kind: 'quantitative' | 'verbal' | string;
  objectives: string[];
  phases: { phase: string; components: Comp[] }[];
  format?: 'phased' | 'claims' | string;
  claims?: Claim[];
  reference?: Ref[];
};
type Comp = { component_id: string; type: string; title: string; data: any };
type Claim = {
  id: string; type: string; claim: string; detail?: string; builds_on?: string[];
  figure?: any;
};
type Ref = { id: string; title: string; markdown: string };

/**
 * Terminology is shared with the ESSLCE lessons tab on purpose. A student who
 * uses both packages should not have to learn two vocabularies for the same
 * thing — "Practice" and "Quiz" already mean something specific in Lomi, and
 * inventing "Drill" and "Check yourself" beside them makes the product feel
 * like two products.
 */
/**
 * What a student is looking at, named by COMPONENT TYPE.
 *
 * The navigation used to be built from authoring phases — "Why it matters",
 * "How it works", "How to spot it". Those describe what the writer was doing
 * in one particular lesson, so they can never be shared buttons: they would
 * read differently on every page, and no student has ever thought "I am
 * orienting now".
 *
 * Component types are the real shared vocabulary, and it is the one ESSLCE
 * already uses — Key Terms, Practice, Quiz. A student who uses both packages
 * meets one product, and a lesson's sections stop being named after the
 * process that produced them.
 */
const SECTION_LABEL: Record<string, { label: string; icon: string }> = {
  key_definitions: { label: 'Key Terms', icon: '📖' },
  lesson_content: { label: 'Key Terms', icon: '📖' },
  exam_trap: { label: 'Exam Traps', icon: '💡' },
  practice_mcq: { label: 'Practice', icon: '✏️' },
  assessment_set: { label: 'Quiz', icon: '📋' },
};

/**
 * Reading order is by IMPORTANCE, not by the order the author wrote in.
 *
 * The phases came out of the pipeline as ORIENT -> LEARN -> RECOGNISE, so the
 * page opened with "Good to Know" context and buried the Must Knows below it.
 * That is backwards for a reference surface: a student opening Key Terms two
 * days before the exam wants the load-bearing facts first, and the student who
 * wants the framing can scroll to it.
 *
 * Practice and Quiz always stay last — they are a change of activity, not
 * reading, and they close the lesson.
 */
const READING_ORDER: Record<string, number> = {
  LEARN: 0,      // ⭐ Must Know
  MODEL: 1,      // ⭐ Must Know — worked examples
  RECOGNISE: 2,  // How to spot it
  ORIENT: 3,     // Why it matters — context, not a fact to carry in
};

const orderedPhases = (lesson: Lesson) =>
  [...lesson.phases].sort((a, b) => {
    const pa = IS_PRACTICE(a.phase) ? 100 : READING_ORDER[a.phase] ?? 50;
    const pb = IS_PRACTICE(b.phase) ? 100 : READING_ORDER[b.phase] ?? 50;
    if (pa !== pb) return pa - pb;
    // DRILL before PROVE, so practice precedes the quiz.
    return a.phase === 'DRILL' ? -1 : b.phase === 'DRILL' ? 1 : 0;
  });

/** Loose comparison for "is this heading just repeating the first card?" */
const plainish = (t: string) =>
  (t || '').toLowerCase().replace(/[^a-z0-9]/g, '');

/** The sections of a lesson, in reading order, named by what they contain. */
const sectionsOf = (lesson: Lesson) => {
  const out: { key: string; label: string; icon: string; phases: string[] }[] = [];
  for (const p of orderedPhases(lesson)) {
    for (const c of p.components) {
      const s = SECTION_LABEL[c.type];
      if (!s) continue;
      const found = out.find((x) => x.key === s.label);
      if (found) {
        if (!found.phases.includes(p.phase)) found.phases.push(p.phase);
      } else {
        out.push({ key: s.label, label: s.label, icon: s.icon, phases: [p.phase] });
      }
    }
  }
  return out;
};

/** Practice sections get visual separation from reading; the rest do not. */
const IS_PRACTICE = (p: string) => p === 'DRILL' || p === 'PROVE';

/* ---------------------------------------------------------------- prose --- */

/* ------------------------------------------------------- the card format --- */

/**
 * The reading surface, rebuilt on the shape students already love.
 *
 * Lifted deliberately — layout, colours and mechanics — from the ESSLCE
 * `key_definitions` component, which is the most-liked reading surface in
 * Lomi. Its anatomy, and why each part earns its place:
 *
 *   a named handle          you can point at it, unlike a paragraph
 *   a one-line payoff       visible WITHOUT expanding anything
 *   a triage badge          the anti-fear mechanism, and the part most often
 *                           mistaken for decoration: a student opening a page
 *                           sees there are four Must Knows, so the scope stops
 *                           being unknown before any reading happens
 *   detail on demand        opt-in depth, never a wall
 *   Amharic at BOTH levels  so a student reading in Amharic never has to
 *                           expand anything to be served
 *   per-item actions        Ask Lomi attached to THIS point, not the page
 *
 * ## What changed for UAT
 *
 * A UAT lesson has no "terms" — it has claims about how a question type
 * behaves. So the mapping is: the one-line `point` becomes the handle, its
 * `detail` becomes the visible payoff (99–154 characters — short enough to
 * read without deciding to), and the block's PROSE is demoted to "Explain
 * more". The prose is not deleted; it stops being the first thing you meet.
 *
 * That inversion is the whole change. The material was always this shape —
 * every lesson_content block already carries key_points — it was simply
 * rendered underneath 3,600 characters of prose and reachable only through a
 * density toggle nobody finds.
 */
const PRIORITY_BY_PHASE: Record<string, { rank: 'high' | 'medium' | 'low'; label: string }> = {
  // Derived from the authoring phase, which is the only priority signal the
  // current content carries. When the generation pipeline is rebuilt this
  // should become an explicit field on the point rather than an inference —
  // an author knows what is load-bearing better than a phase name does.
  LEARN: { rank: 'high', label: '⭐ Must know' },
  MODEL: { rank: 'high', label: '⭐ Must know' },
  RECOGNISE: { rank: 'medium', label: 'How to spot it' },
  ORIENT: { rank: 'low', label: 'Why it matters' },
};

/**
 * One card. Deliberately the same contract as the ESSLCE `key_definitions`
 * item, field for field:
 *
 *     term                    brief_definition           detailed_explanation
 *     memorization_priority   exam_tip                   figure
 *
 * The generator will emit exactly these. Until it does, `point` / `detail` /
 * the block's prose are mapped onto them by the caller, and the priority falls
 * back to an inference from the authoring phase — so the surface is finished
 * and the content grows into it, rather than the surface waiting on content.
 */
const PRIORITY_LABEL: Record<string, string> = {
  high: '⭐ Must Know',
  medium: 'Important',
  low: 'Good to Know',
};

const PointCard: React.FC<{
  term: string;
  brief?: string;
  detailed?: string;
  examTip?: string;
  priority?: 'high' | 'medium' | 'low';
  figure?: any;
  cacheKey: string;
  subject?: string;
  moduleId?: string;
  onAskLomi?: (q: any) => void;
  onAskWith?: (prompt: string, source?: { type: string; content: any }) => void;
}> = ({
  term, brief, detailed, examTip, priority = 'medium', figure,
  cacheKey, subject, moduleId, onAskLomi, onAskWith,
}) => {
  const [open, setOpen] = useState(false);
  const more = detailed;

  return (
    <div className={`kp-card priority-${priority}${open ? ' expanded' : ''}`}>
      <div className="kp-head">
        <div className="kp-term">
          <MarkdownRenderer inline>{term}</MarkdownRenderer>
        </div>
        <span className={`kp-badge priority-${priority}`}>
          {PRIORITY_LABEL[priority]}
        </span>
      </div>

      {brief && (
        <div className="kp-meaning">
          <MarkdownRenderer inline>{brief}</MarkdownRenderer>
        </div>
      )}

      {/* Amharic on the summary itself, available without expanding — exactly
          as the ESSLCE component does it, and with a distinct cacheKey so it
          cannot collide with the detailed explanation's translation below. */}
      {brief && (
        <TranslateAmharicButton
          text={brief}
          surface="package_point_summary"
          cacheKey={`${cacheKey}:summary`}
          compact
        />
      )}

      {figure && <ContentFigure figure={figure} />}

      {more && (
        <>
          <button
            className={`kp-more${open ? ' expanded' : ''}`}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
          >
            {open ? '▼ Show Less' : '▶ Explain More'}
          </button>
          {open && (
            <div className="kp-detail">
              <MarkdownRenderer>{more}</MarkdownRenderer>
              <TranslateAmharicButton
                text={more}
                surface="package_point_detail"
                cacheKey={cacheKey}
                compact
              />
            </div>
          )}
        </>
      )}

      {examTip && (
        <div className="kp-examtip">
          <span className="kp-tipicon">💡</span>
          <strong>Exam tip:</strong>{' '}
          <MarkdownRenderer inline>{examTip}</MarkdownRenderer>
        </div>
      )}

      {/* The follow-up labels ride with the EXPANSION, not the collapsed card.
          Ten cards each showing Amharic + Explain More + Explain + Quiz me put
          forty controls down one page and buried the content they belonged to.
          A student who has opened a card is the one who might want to ask
          about it; a student scanning the list is not.

          Fetched from the same table that drives them on the ESSLCE lessons
          tab, so a trigger configured for `key_definitions` appears here too —
          one place to edit the prompts students actually tap. */}
      {open && (
      <ComponentTriggers
        componentType="key_definitions"
        triggerLocation="definition_item"
        subject={subject}
        templateVars={{ term }}
        sourceComponentType="key_definition"
        sourceComponentContent={{ term, definition: brief, module_id: moduleId }}
        onTriggerClick={(prompt: string, sourceComponent?: any) =>
          onAskWith
            ? onAskWith(prompt, sourceComponent)
            : onAskLomi?.({ question: prompt })
        }
        className="kp-triggers"
        fallbackTriggers={[
          { label: '💬 Explain', prompt: `Explain "${term}" in simpler terms.` },
          {
            label: '❓ Quiz me',
            prompt: `Quiz me on "${term}" — give me a quick question to test if I understand it.`,
          },
        ]}
      />
      )}
    </div>
  );
};

/** A trap or caution — same card, different accent. Kept visually distinct
 *  because "where marks go" is the one thing a student should not skim. */
const TrapCard: React.FC<{ c: Comp; cacheKey: string }> = ({ c, cacheKey }) => {
  const md = c.data?.content_markdown;
  if (!md) return null;
  return (
    <div className="kp-card kp-card--trap">
      <div className="kp-head">
        <div className="kp-term">{c.title || 'Where marks go'}</div>
        <span className="kp-badge priority-high">💡 Exam trap</span>
      </div>
      <div className="kp-trapbody">
        <MarkdownRenderer>{md}</MarkdownRenderer>
      </div>
      <TranslateAmharicButton text={md} surface="package_trap" cacheKey={cacheKey} compact />
    </div>
  );
};

/** A block with no authored key points. Renders its prose rather than an
 *  empty space — missing content must never look like missing knowledge. */
const Prose: React.FC<{ c: Comp; brief: boolean }> = ({ c, brief }) => {
  const md = c.data?.content_markdown;
  if (!md) return null;

  return (
    <div className="ps-prose">
      <MarkdownRenderer>{md}</MarkdownRenderer>
      <ContentFigure figure={c.data?.figure} />
      <div className="ps-blockbar">
        <TranslateAmharicButton
          text={md}
          surface="package_lesson_section"
          cacheKey={`${c.component_id}:full`}
          compact
        />
      </div>
    </div>
  );
};

/* ----------------------------------------------------------------- item --- */

/**
 * One multiple-choice item. Answering reveals the explanation AND, for a wrong
 * pick, the note for that specific distractor — with no textbook to check us
 * against, "why this one is wrong" is how the product earns trust.
 */
const Item: React.FC<{
  q: any;
  compact?: boolean;
  /** The label matters as much as the verdict — the server records the choice,
   *  not whether it happened to be right. */
  onAnswered?: (ok: boolean, label: string) => void;
  onAskLomi?: (q: any) => void;
  onSimilar?: (q: any) => void;
}> = ({ q, compact, onAnswered, onAskLomi, onSimilar }) => {
  const [picked, setPicked] = useState<string | null>(null);
  const options: { label: string; text: string }[] = q?.options || [];
  const correct = q?.correct_answer;

  const choose = (label: string) => {
    if (picked) return;
    setPicked(label);
    onAnswered?.(label === correct, label);
  };

  return (
    <div className={`ps-item${compact ? ' ps-item--compact' : ''}`}>
      <div className="ps-q">
        <MarkdownRenderer>{q?.question || ''}</MarkdownRenderer>
        <ContentFigure figure={q?.figure} />
      </div>
      <div className="ps-opts">
        {options.map((o) => {
          const state =
            !picked ? '' : o.label === correct ? ' is-right'
            : o.label === picked ? ' is-wrong' : '';
          return (
            <button
              key={o.label}
              className={`ps-opt${state}`}
              onClick={() => choose(o.label)}
              disabled={!!picked}
            >
              <span className="ps-lab">{o.label}</span>
              <span className="ps-otext">
                <MarkdownRenderer inline>{o.text}</MarkdownRenderer>
              </span>
            </button>
          );
        })}
      </div>
      {picked && (
        <div className="ps-verdict">
          <MarkdownRenderer>{q?.explanation || ''}</MarkdownRenderer>
          {picked !== correct && q?.distractor_notes?.[picked] && (
            <div className="ps-why">
              <MarkdownRenderer>{q.distractor_notes[picked]}</MarkdownRenderer>
            </div>
          )}
          <div className="ps-blockbar">
            <TranslateAmharicButton
              text={q?.explanation || ''}
              surface="package_item_explanation"
              cacheKey={`${q?.question?.slice(0, 40)}:expl`}
              compact
            />
            {onAskLomi && (
              <button className="ps-chip" onClick={() => onAskLomi(q)}>
                💬 Ask Lomi
              </button>
            )}
            {onSimilar && (
              <button className="ps-chip" onClick={() => onSimilar(q)}>
                ↻ Another like this
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const itemsOf = (c: Comp): any[] =>
  c.type === 'assessment_set' ? c.data?.questions || [] : [c.data];

/** Quantitative: one drill at a time. An item can take three minutes and the
 *  student needs the working space; a wall of them reads as homework. */
const QuantDrills: React.FC<Body> = ({ comps, onAskLomi, onSimilar, onRecord }) => {
  const all = comps.flatMap(itemsOf);
  const [i, setI] = useState(0);
  return (
    <div className="ps-drills">
      <div className="ps-drillhead">
        Item {Math.min(i + 1, all.length)} of {all.length}
      </div>
      <Item
        q={all[i]}
        onAnswered={(ok) => onRecord?.(all[i], ok)}
        onAskLomi={onAskLomi}
        onSimilar={onSimilar}
      />
      <div className="ps-row">
        <button className="ps-btn ghost" disabled={i === 0} onClick={() => setI(i - 1)}>
          ← Previous
        </button>
        <button
          className="ps-btn"
          disabled={i >= all.length - 1}
          onClick={() => setI(i + 1)}
        >
          Next item →
        </button>
      </div>
    </div>
  );
};

/** Verbal: every item visible, answered in a run. Twenty seconds each — the
 *  reps ARE the method, and paging between them destroys the rhythm. */
const VerbalDrills: React.FC<Body> = ({ comps, onAskLomi, onSimilar, onRecord }) => {
  const all = comps.flatMap(itemsOf);
  const [done, setDone] = useState(0);
  return (
    <div className="ps-drills">
      <div className="ps-drillhead">
        {done} of {all.length} answered · keep going, they are quick
      </div>
      {all.map((q, n) => (
        <Item
          key={n}
          q={q}
          compact
          onAnswered={(ok) => {
            setDone((d) => d + 1);
            onRecord?.(q, ok);
          }}
          onAskLomi={onAskLomi}
          onSimilar={onSimilar}
        />
      ))}
    </div>
  );
};

type Body = {
  comps: Comp[];
  onAskLomi?: (q: any) => void;
  onSimilar?: (q: any) => void;
  onRecord?: (q: any, ok: boolean) => void;
  /** The package's subject, so per-card triggers resolve the same way they do
   *  on the ESSLCE lessons tab. */
  subject?: string;
  /** Open Ask Lomi with a specific prompt AND the item it came from — the
   *  contract ComponentTriggers hands back. */
  onAskWith?: (prompt: string, source?: { type: string; content: any }) => void;
};

const PhaseBody: React.FC<
  { lesson: Lesson; phase: string; brief: boolean } & Body
> = ({ lesson, phase, comps, brief, onAskLomi, onSimilar, onRecord, subject, onAskWith }) => {
  const isDrill = phase === 'DRILL' || phase === 'PROVE';
  if (!isDrill) {
    return (
      <>
        {comps.map((c) => {
          const pts: any[] = c.data?.key_points || [];
          const md: string = c.data?.content_markdown || '';

          // A trap is not a point — it is the thing you lose marks to, and it
          // gets its own accent so it cannot be skimmed past.
          if (c.type === 'exam_trap') {
            return <TrapCard key={c.component_id} c={c} cacheKey={c.component_id} />;
          }

          // No points authored for this block: fall back to prose rather than
          // render an empty card list. Missing content must never look like
          // missing knowledge.
          if (pts.length === 0) {
            return <Prose key={c.component_id} c={c} brief={brief} />;
          }

          const pr = PRIORITY_BY_PHASE[phase];
          return (
            <div key={c.component_id} className="kp-group">
              {/* One heading per section. The badge that used to sit above
                  this said "Key Terms" — which the tab, the section strip and
                  this heading were all already saying. And when the authored
                  title merely repeats the first card, it is dropped rather
                  than shown twice. */}
              {c.title && plainish(c.title) !== plainish(pts[0]?.point || pts[0]?.term || '') && (
                <h2 className="kp-grouptitle">{c.title}</h2>
              )}
              {pts.map((p, i) => (
                <PointCard
                  key={i}
                  // The generator will emit these names directly. Until then
                  // the current fields are mapped onto them here — one place,
                  // and it disappears when the content catches up.
                  term={p.term || p.point}
                  brief={p.brief_definition || p.detail}
                  detailed={p.detailed_explanation || md}
                  examTip={p.exam_tip}
                  priority={p.memorization_priority || pr?.rank || 'medium'}
                  figure={p.figure}
                  cacheKey={`${c.component_id}:kp:${i}`}
                  subject={subject}
                  moduleId={lesson.module_id}
                  onAskLomi={onAskLomi}
                  onAskWith={onAskWith}
                />
              ))}
            </div>
          );
        })}
      </>
    );
  }
  // Drills are never abbreviated. Brief shortens what you READ, not what you
  // practise — a student in revision mode wants fewer words and MORE reps.
  return lesson.kind === 'verbal' ? (
    <VerbalDrills comps={comps} onAskLomi={onAskLomi} onSimilar={onSimilar} onRecord={onRecord} />
  ) : (
    <QuantDrills comps={comps} onAskLomi={onAskLomi} onSimilar={onSimilar} onRecord={onRecord} />
  );
};

/**
 * The quiz, delivered the way the ESSLCE lessons tab delivers one: one question
 * per screen, a progress bar, and Continue only after you have answered.
 *
 * This is the opposite of how the lesson BODY is laid out, and deliberately so.
 * Component-per-screen is wrong for reading — it destroys the ability to look
 * back at the derivation while you work — and right for quizzing, where the
 * point is to commit to an answer before seeing the next thing. The same
 * finding, applied twice in opposite directions.
 */
const GuidedQuiz: React.FC<{
  items: any[];
  onAskLomi: (q?: any) => void;
  onSimilar: (q: any) => void;
  onRecord?: (q: any, ok: boolean) => void;
  onDone: () => void;
}> = ({ items, onAskLomi, onSimilar, onRecord, onDone }) => {
  const [i, setI] = useState(0);
  const [answered, setAnswered] = useState<Record<number, boolean>>({});
  const [score, setScore] = useState(0);
  const last = i >= items.length - 1;

  if (items.length === 0) return null;

  return (
    <div className="ps-quiz">
      <div className="ps-quizbar">
        <span className="ps-quizcount">
          Question {i + 1} of {items.length}
        </span>
        <span className="ps-quizscore">
          {score} correct
        </span>
      </div>
      <div className="ps-quizprog">
        <i style={{ width: `${((i + (answered[i] !== undefined ? 1 : 0)) / items.length) * 100}%` }} />
      </div>

      <Item
        key={i}
        q={items[i]}
        onAnswered={(ok) => {
          setAnswered((a) => ({ ...a, [i]: ok }));
          if (ok) setScore((n) => n + 1);
        }}
        onAskLomi={onAskLomi}
        onSimilar={onSimilar}
      />

      <div className="ps-row ps-sticky">
        <button className="ps-btn ghost" disabled={i === 0} onClick={() => setI(i - 1)}>
          ← Back
        </button>
        {answered[i] === undefined ? (
          <span className="ps-small ps-muted">Choose an answer</span>
        ) : last ? (
          <button className="ps-btn" onClick={onDone}>
            Finish · {score} of {items.length}
          </button>
        ) : (
          <button className="ps-btn" onClick={() => setI(i + 1)}>
            Continue →
          </button>
        )}
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------- claims -- */

const CLAIM_GROUP: { type: string; label: string | null }[] = [
  // No label on the opening: a lesson does not need to announce that it is
  // starting, and the first thing on the page should be content.
  { type: 'stakes', label: null },
  { type: 'principle', label: 'The ideas' },
  { type: 'method', label: 'The method' },
  { type: 'caution', label: 'Where marks go' },
];

/**
 * The claims body — the format experiment.
 *
 * Three differences from the phased body, each answering something the phased
 * version could not do:
 *
 * 1. The SPINE is claims, not prose. One idea per row, detail on demand. Brief
 *    and Full stop being two documents: Full is just every row expanded, which
 *    is why the density toggle drives `openAll` here rather than swapping
 *    content.
 * 2. REFERENCE is a plane, not a phase. It sits in a drawer reachable from
 *    anywhere, because a shape table is something you consult mid-drill — the
 *    phased version had to be scrolled back to.
 * 3. ITEMS route. A wrong answer names the claim it violated and offers the
 *    other items sharing its misconception, which is what a repair queue needs
 *    and what "topic" as the only index could never provide.
 */
const ClaimsBody: React.FC<
  { lesson: Lesson; brief: boolean } & Body
> = ({ lesson, brief, comps, onAskLomi, onSimilar }) => {
  const claims = lesson.claims || [];
  const reference = lesson.reference || [];
  const items = comps.flatMap((c) =>
    c.type === 'assessment_set' ? c.data?.questions || [] : [c.data]
  );

  const [open, setOpen] = useState<Set<string>>(new Set());
  const [refOpen, setRefOpen] = useState(false);
  const [focus, setFocus] = useState<string | null>(null);
  // The other direction of the link. An item already names the claim it tests;
  // without this, a claim is a dead end and the two rooms stay disconnected —
  // the student does the hunting, which is exactly what "go back to the lesson
  // when you need to" is supposed to avoid.
  const [claimFocus, setClaimFocus] = useState<string | null>(null);

  const isOpen = (id: string) => !brief || open.has(id);
  const toggle = (id: string) =>
    setOpen((p) => {
      const n = new Set(p);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const claimById = (id?: string | null) => claims.find((c) => c.id === id);
  const shown = claimFocus
    ? items.filter((i: any) => i.tests_claim === claimFocus)
    : focus
    ? items.filter((i: any) => i.misconception === focus)
    : items;
  const countFor = (id: string) =>
    items.filter((i: any) => i.tests_claim === id).length;

  return (
    <div className="ps-claims">
      <div className="ps-refbar">
        <button className={`ps-chip${refOpen ? ' on' : ''}`} onClick={() => setRefOpen((v) => !v)}>
          📑 Reference · {reference.length}
        </button>
        <span className="ps-hint">You can reopen the table at any time.</span>
      </div>
      {refOpen && (
        <div className="ps-refplane">
          {reference.map((r) => (
            <div key={r.id} className="ps-refcard">
              <h4 className="ps-reftitle">{r.title}</h4>
              <div className="ps-prose">
                <MarkdownRenderer>{r.markdown}</MarkdownRenderer>
                <ContentFigure figure={(r as any).figure} />
              </div>
              <TranslateAmharicButton
                text={r.markdown}
                surface="package_reference"
                cacheKey={`${lesson.module_id}:ref:${r.id}`}
                compact
              />
            </div>
          ))}
        </div>
      )}

      {CLAIM_GROUP.map((g) => {
        const rows = claims.filter((c) => c.type === g.type);
        if (rows.length === 0) return null;
        return (
          <section key={g.type} className="ps-section">
            {g.label && <h3 className="ps-phase">{g.label}</h3>}
            <ul className="ps-kp">
              {rows.map((c) => (
                <li
                  key={c.id}
                  id={`claim-${c.id}`}
                  className={`ps-kpitem${isOpen(c.id) ? ' is-open' : ''}`}
                >
                  <button
                    className="ps-kphead"
                    onClick={() => toggle(c.id)}
                    aria-expanded={isOpen(c.id)}
                  >
                    <span className="ps-kpcaret">{isOpen(c.id) ? '▾' : '▸'}</span>
                    <span className="ps-kptext">
                      <MarkdownRenderer inline>{c.claim}</MarkdownRenderer>
                    </span>
                  </button>
                  {isOpen(c.id) && c.detail && (
                    <div className="ps-kpdetail">
                      <MarkdownRenderer>{c.detail}</MarkdownRenderer>
                      <ContentFigure figure={(c as any).figure} />
                      <div className="ps-blockbar">
                        <TranslateAmharicButton
                          text={`${c.claim}\n\n${c.detail}`}
                          surface="package_claim"
                          cacheKey={`${lesson.module_id}:${c.id}`}
                          compact
                        />
                        <button className="ps-chip" onClick={() => onAskLomi?.({ question: c.claim })}>
                          💬 Ask about this
                        </button>
                        {countFor(c.id) > 0 && (
                          <button
                            className="ps-chip"
                            onClick={() => {
                              setClaimFocus(c.id);
                              setFocus(null);
                              document
                                .getElementById('ps-practice')
                                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }}
                          >
                            ✍️ Practise this · {countFor(c.id)}
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      <section className="ps-section" id="ps-practice">
        <h3 className="ps-phase">
          Practice
          {claimFocus ? ' · one idea' : focus ? ` · one mistake` : ''}
        </h3>
        {(focus || claimFocus) && (
          <button
            className="ps-chip"
            onClick={() => {
              setFocus(null);
              setClaimFocus(null);
            }}
          >
            ← all {items.length} items
          </button>
        )}
        <div className="ps-drills">
          {shown.map((q: any, n: number) => {
            const c = claimById(q.tests_claim);
            return (
              <div key={n} className="ps-tagged">
                <Item q={q} compact onAskLomi={onAskLomi} onSimilar={onSimilar} />
                <div className="ps-tags">
                  {c && (
                    <button
                      className="ps-tag"
                      onClick={() =>
                        document
                          .getElementById(`claim-${c.id}`)
                          ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
                      }
                      title="Review the concept"
                    >
                      tests: {c.claim.slice(0, 44)}{c.claim.length > 44 ? '…' : ''}
                    </button>
                  )}
                  {q.misconception && (
                    <button
                      className="ps-tag ps-tag--miss"
                      onClick={() => {
                      setFocus(q.misconception);
                      setClaimFocus(null);
                    }}
                    >
                      more like this: {q.misconception.replace(/-/g, ' ')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
};

/* ---------------------------------------------------------------- shells -- */

type ShellProps = {
  /** Threaded to the cards so per-item Ask Lomi triggers resolve for this
   *  package's subject, exactly as they do on the ESSLCE lessons tab. */
  subject?: string;
  onAskWith?: (prompt: string, source?: { type: string; content: any }) => void;
  lesson: Lesson;
  brief: boolean;
  onAskLomi: (q?: any) => void;
  onSimilar: (q: any) => void;
  onRecord: (q: any, ok: boolean) => void;
};

/**
 * The lesson body — one column.
 *
 * The left rail is gone. It was a remnant of the original "reference shell",
 * and it had stopped earning its place twice over: on desktop it duplicated
 * the section badge, and on mobile it collapsed into a chip strip that said
 * the same words a second time, directly above them. A lesson with three
 * sections does not need a table of contents; it needs to start.
 */
const ShellReference: React.FC<ShellProps> = ({ lesson, brief, onAskLomi, onSimilar, onRecord, subject, onAskWith }) => (
  <div className="ps-reader">
    {orderedPhases(lesson).map((p) => (
      <section
        key={p.phase}
        id={`sec-${p.phase}`}
        className={`ps-section${IS_PRACTICE(p.phase) ? ' ps-section--practice' : ''}`}
      >
        {IS_PRACTICE(p.phase) && (
          <h3 className="ps-phase">{p.phase === 'PROVE' ? 'Quiz' : 'Practice'}</h3>
        )}
        <PhaseBody
          lesson={lesson}
          phase={p.phase}
          comps={p.components}
          brief={brief}
          onAskLomi={onAskLomi}
          onSimilar={onSimilar}
          onRecord={onRecord}
          subject={subject}
          onAskWith={onAskWith}
        />
      </section>
    ))}
  </div>
);

const ShellTimed: React.FC<ShellProps> = ({ lesson, brief, onAskLomi, onSimilar, onRecord, subject, onAskWith }) => {
  // Until the mock runner exists (stage 2) the marks are illustrative — labelled
  // as such rather than presented as if a real paper produced them.
  const marks = lesson.kind === 'verbal' ? 11 : 6;
  const [open, setOpen] = useState(false);
  return (
    <div className="ps-repair">
      <div className="ps-repairhead">
        <span className="ps-lost">−{marks} marks</span>
        <div>
          <div className="ps-repairtitle">{lesson.title}</div>
          <div className="ps-repairsub">
            Queued from your mock · {lesson.minutes} min ·{' '}
            <span className="ps-illus">illustrative until the mock runner ships</span>
          </div>
        </div>
      </div>
      <div className="ps-callout">
        You lost {marks} marks here. This repair covers only what the paper says
        you got wrong — read it, then prove it.
      </div>
      {lesson.phases
        .filter((p) => (open ? true : p.phase !== 'ORIENT' && p.phase !== 'RECOGNISE'))
        .map((p) => (
          <section
            key={p.phase}
            className={`ps-section${IS_PRACTICE(p.phase) ? ' ps-section--practice' : ''}`}
          >
            {IS_PRACTICE(p.phase) && (
              <h3 className="ps-phase">
                {p.phase === 'PROVE' ? 'Quiz' : 'Practice'}
              </h3>
            )}
            <PhaseBody
              lesson={lesson}
              phase={p.phase}
              comps={p.components}
              brief={brief}
              onAskLomi={onAskLomi}
              onSimilar={onSimilar}
              onRecord={onRecord}
              subject={subject}
              onAskWith={onAskWith}
            />
          </section>
        ))}
      {!open && (
        <button className="ps-btn ghost" onClick={() => setOpen(true)}>
          Open the full lesson
        </button>
      )}
    </div>
  );
};

const ShellDaily: React.FC<ShellProps> = ({ lesson, brief, onAskLomi, onSimilar, onRecord, subject, onAskWith }) => {
  const [i, setI] = useState(0);
  const p = lesson.phases[i];
  const last = i >= lesson.phases.length - 1;
  return (
    <div className="ps-daily">
      <div className="ps-phases">
        {lesson.phases.map((ph, n) => (
          <React.Fragment key={ph.phase}>
            {n > 0 && <span className="ps-sep">›</span>}
            <span className={`ps-ph${n === i ? ' on' : n < i ? ' done' : ''}`}>
              {ph.phase === 'PROVE' ? 'Quiz' : ph.phase === 'DRILL' ? 'Practice' : 'Key Terms'}
            </span>
          </React.Fragment>
        ))}
      </div>
      <div className="ps-section">
        <PhaseBody
          lesson={lesson}
          phase={p.phase}
          comps={p.components}
          brief={brief}
          onAskLomi={onAskLomi}
          onSimilar={onSimilar}
          onRecord={onRecord}
          subject={subject}
          onAskWith={onAskWith}
        />
      </div>
      <div className="ps-row ps-sticky">
        <button className="ps-btn ghost" disabled={i === 0} onClick={() => setI(i - 1)}>
          ← Back
        </button>
        {last ? (
          <span className="ps-donenote">Done for today.</span>
        ) : (
          <button className="ps-btn" onClick={() => setI(i + 1)}>
            Continue →
          </button>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ page -- */

/**
 * TWO surfaces, not four.
 *
 * The four-shell experiment answered its question: only two of them are places
 * a student goes. "Repair" and "Today" are not rooms — they are decisions
 * about what to show next. Repair is the misses queue with a frame around it;
 * Today is one day's worth of the same thing. Both answer "what now", not
 * "where am I", so both belong in the notification layer rather than the nav.
 *
 * What survives from them is their logic, not their tabs: the daily card
 * becomes an evening push that deep-links here, and the repair ordering is
 * what the misses queue already does.
 *
 * The two remaining rooms are the two real student behaviours — study by
 * doing questions, and go read when you need to. Their names say which.
 */
/**
 * The paper's three parts, in the order it runs them.
 *
 * `micro_learning_modules.topic` carries the section for every UAT lesson
 * ("Quantitative Reasoning"), so the grouping is data, not a hardcoded list of
 * which lesson goes where — a new topic lands in the right group by itself.
 */
//
// ⚠️ This is UAT's paper order, kept ONLY to order UAT's own sections. It is
// not the list of sections that exist. It used to be both, and every lesson
// whose section was not one of these three fell into `SECTION_ORDER[0]` — so
// EAU's fifty lessons all landed in "Quantitative Reasoning", the filter had
// one entry, and the Lessons tab rendered as a flat alphabetical list of
// fifty with no way to narrow it.
const UAT_SECTION_ORDER = [
  'Quantitative Reasoning',
  'Verbal Reasoning',
  'Analytical Reasoning',
] as const;

/** Short names. The section heading does not need to say "Reasoning" three
 *  times down the page. A section not listed here is already short — EAU
 *  names its own "Math", "Physics", "Visual" — and is used as authored. */
const SECTION_NAME: Record<string, string> = {
  'Quantitative Reasoning': 'Quantitative',
  'Verbal Reasoning': 'Verbal',
  'Analytical Reasoning': 'Analytical',
};

const sectionLabel = (sec: string) => SECTION_NAME[sec] || sec;

const SHELLS: Record<
  string,
  { name: string; icon: string; blurb: string }
> = {
  questions: {
    name: 'Questions',
    icon: '✍️',
    blurb: 'Choose a topic and start solving.',
  },
  lessons: {
    name: 'Lessons',
    icon: '📖',
    blurb: 'Learn the key ideas, then practise.',
  },
  /**
   * The sitting earns a nav slot without reopening the four-shell question.
   * Repair and Today lost theirs because they were SEQUENCING — decisions about
   * what to show next, which belong in the notification layer. A sitting is not
   * a sequencing decision and not a lesson layout: it is a distinct thing a
   * student sets aside an hour to go and do, under a clock, in one place. That
   * is what a nav entry is for.
   */
  mock: {
    name: 'Mocks',
    icon: '⏱',
    blurb: 'Sit a full paper under a clock.',
  },
};

/** Old shell keys keep working — links already exist, and the B/C layouts stay
 *  reachable by URL so their comparison data is not thrown away. */
const LEGACY: Record<string, string> = {
  a: 'lessons',
  q: 'questions',
  // "Sitting" was jargon — no student says it. The URL keeps working so any
  // link already shared does not break.
  sitting: 'mock',
};

const PackageShell: React.FC = () => {
  // Questions is the default room, not Lessons. A student arriving days before
  // the exam wants to answer questions; the lesson cards are what they open
  // when a question defeats them. `/p/<pkg>` with no room lands here.
  const { pkg = 'uat-aau', shell: rawShell = 'questions', slug } = useParams();
  const shell = LEGACY[rawShell] || rawShell;
  const navigate = useNavigate();
  const [lessons, setLessons] = useState<any[]>([]);
  /**
   * What this student may open, as the SERVER sees it.
   *
   * The lessons payload has always carried it; the shell dropped it, so
   * every trial-flavoured line here had to infer access from something
   * else — locked lessons, a spent allowance — and the one line that had
   * neither to infer from (the mock row) went on calling itself free to
   * students who had paid. Granting from the admin dashboard must be the
   * only switch that matters, so read the verdict instead of guessing it.
   */
  const [access, setAccess] = useState<string>('free');
  const [pool, setPool] = useState<{
    subjects: Array<{ subject: string; questions: number }>;
    /** The free-tier verdict, already travelling with the pool payload. */
    allowance?: {
      access: string;
      limit: number | null;
      used: number;
      remaining: number | null;
      /** True when the free set is empty because the package has no
       *  content — as opposed to the student having spent it. */
      empty?: boolean;
    } | null;
  }>({ subjects: [] });
  // A package holds SUBJECTS, plural. UAT has one, ESSLCE has six — so the
  // chooser exists always and simply collapses when there is nothing to choose.
  const [subject, setSubject] = useState<string | null>(null);
  const [prog, setProg] = useState<Awaited<
    ReturnType<typeof packagesAPI.progress>
  > | null>(null);
  // The name belongs to the program row. The map below is only the first-paint
  // fallback, used until the catalog answers.
  //
  // It used to be the WHOLE answer — the comment claimed the catalog corrected
  // it and nothing ever fetched the catalog — so any package not in this
  // ternary rendered its own program code as its title. EAU would have opened
  // a room headed "eau-entrance". Every new package hitting that is the reason
  // the name is now read rather than listed.
  const FALLBACK_NAME: Record<string, string> = {
    'uat-aau': 'AAU UAT Prep',
    'eau-entrance': 'EAU Prep',
  };
  const [catalogName, setCatalogName] = useState<string | null>(null);
  const pkgName = catalogName || FALLBACK_NAME[pkg] || pkg;

  useEffect(() => {
    let cancelled = false;
    packagesAPI
      .list()
      .then((d) => {
        if (cancelled) return;
        const card = (d.packages || []).find((c) => c.key === pkg);
        if (card?.name) setCatalogName(card.name);
      })
      // A missing name is cosmetic — the fallback above is already showing.
      // Never let it take the room down.
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [pkg]);
  const [lesson, setLesson] = useState<Lesson | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Collapsed is the DEFAULT, and this one line was most of the complaint.
  //
  // The card format is the lesson now, so `brief` no longer swaps documents —
  // it only decides whether the claims format opens its rows expanded
  // (`isOpen = !brief || open.has(id)`). Defaulting to false meant the claims
  // spine rendered with every detail already open, i.e. its worst possible
  // presentation, which is exactly the wall of text this rebuild removes.
  //
  // Respect a stored preference, but default the unset case to collapsed.
  const [brief, setBrief] = useState<boolean>(
    () => localStorage.getItem('ps_density') !== 'full'
  );
  const setDensity = (b: boolean) => {
    setBrief(b);
    localStorage.setItem('ps_density', b ? 'brief' : 'full');
    // Whether students actually want the short version is a real question and
    // this is the only way it gets answered.
    track('package.density', { package: pkg, shell, mode: b ? 'brief' : 'full' });
  };

  const [quiz, setQuiz] = useState(false);
  // A paper in progress owns the whole screen. Leaving the breadcrumb and a nav
  // offering two other rooms on top of a running clock invites exactly the
  // wandering the sitting is meant to train out.
  const [inSitting, setInSitting] = useState(false);
  const [ask, setAsk] = useState(false);
  const [askQ, setAskQ] = useState<any>(null);
  const [askInitial, setAskInitial] = useState<
    { question: string; source_component?: { type: string; content: any } } | undefined
  >();

  /**
   * Record a lesson answer the same way the Questions room does.
   *
   * Answering inside a lesson used to earn nothing — the items had no
   * question_id, so there was no row to record against. Now they do, and a
   * lesson answer feeds the identical path: mastery, spaced review, combos,
   * rank, and the evening push. The two rooms stop having separate progress.
   *
   * The session is created lazily on the FIRST answer rather than on page
   * load, so opening a lesson to read does not manufacture a practice session
   * the student never had — that would inflate activity metrics and, worse,
   * put a combo streak on someone who answered nothing.
   */
  const sessionRef = React.useRef<string | null>(null);
  const startedRef = React.useRef(false);

  const recordAnswer = useCallback(
    async (q: any, ok: boolean) => {
      const qid = q?.question_id;
      const studentId = localStorage.getItem('student_id');
      if (!qid || !studentId || !lesson) return;
      try {
        if (!sessionRef.current && !startedRef.current) {
          startedRef.current = true;
          const ids = lesson.phases
            .flatMap((p) => p.components)
            .flatMap((c: any) =>
              c.data?.questions || (c.data?.question ? [c.data] : [])
            )
            .map((x: any) => x.question_id)
            .filter(Boolean);
          const started = await pastExamAPI.startSession({
            student_id: studentId,
            subject: q.category === 'Verbal Reasoning' || q.topic ? 'UAT' : 'UAT',
            topic: q.topic,
            question_ids: ids,
          });
          sessionRef.current = started?.session_id || started?.id || null;
        }
        if (sessionRef.current) {
          await pastExamAPI.saveAnswer(sessionRef.current, {
            student_id: studentId,
            question_id: qid,
            selected_answer: ok ? q.correct_answer : 'x',
            is_correct: ok,
            time_spent_seconds: 0,
            topic: q.topic,
          });
        }
      } catch {
        // Recording must never break the lesson. A lost event costs a stat;
        // a thrown error costs the student their place on the page.
      }
    },
    [lesson]
  );

  const openAsk = (q?: any) => {
    track('package.asklomi.open', { package: pkg, shell, from: q ? 'item' : 'page' });
    setAskQ(q || null);
    setAskInitial(undefined);
    setAsk(true);
  };
  const askSimilar = (q: any) => {
    track('package.similar.request', { package: pkg, shell });
    setAskQ(q);
    // Same contract MicroLearningModule uses for its "Similar Question" chip,
    // so the tutor gets the item it is generating from rather than a bare
    // instruction — students already use and like this on the ESSLCE side.
    setAskInitial({
      question: 'Generate a similar practice question',
      source_component: { type: 'practice_mcq', content: q },
    });
    setAsk(true);
  };

  /**
   * Open Ask Lomi from a per-card trigger.
   *
   * The triggers hand back both the prompt and the item it came from, and both
   * matter: the prompt is what the student tapped, the source is what stops the
   * tutor answering about the page in general. Identical contract to the
   * ESSLCE lessons tab, so a trigger row configured once serves both.
   */
  const askWith = (
    prompt: string,
    source?: { type: string; content: any }
  ) => {
    track('package.trigger.click', { package: pkg, shell, prompt: prompt.slice(0, 60) });
    setAskQ(source?.content || null);
    setAskInitial({ question: prompt, source_component: source });
    setAsk(true);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      // A slug is a LESSON slug in every room but the mock room, where it is a
      // form code (`/p/<pkg>/mock/VA_MOCK_B`). Fetching it as a lesson 404s,
      // and the error branch above returns before the mock room ever renders —
      // so opening any paper showed "No such lesson".
      if (slug && shell !== 'mock') setLesson(await packagesAPI.lesson(pkg, slug));
      else if (!slug) {
        const [ls, pr, pg] = await Promise.all([
          packagesAPI.lessons(pkg),
          packagesAPI.practice(pkg).catch(() => ({ subjects: [] })),
          packagesAPI.progress(pkg).catch(() => null),
        ]);
        setLessons(ls.lessons);
        setAccess(ls.access || 'free');
        setPool(pr);
        setProg(pg);
        if (pr.subjects.length === 1) setSubject(pr.subjects[0].subject);
      }
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Not available.');
    } finally {
      setLoading(false);
    }
  }, [pkg, slug, shell]);

  useEffect(() => {
    load();
  }, [load]);

  // Four shells exist so one can be chosen on evidence rather than taste, and
  // that only works if what students actually do is recorded. Each event
  // carries the package and shell, so usage can be compared per surface.
  useEffect(() => {
    track('package.shell.view', { package: pkg, shell, slug: slug || null });
  }, [pkg, shell, slug]);

  // "Quiz me" is not a new content type — it is the lesson's own DRILL and
  // PROVE items pulled out of the reading flow. Students already tell us they
  // like the quizzes; this makes them reachable without scrolling past the
  // teaching to find them.
  const quizItems = useMemo(() => {
    if (!lesson) return [];
    return lesson.phases
      .filter((p) => p.phase === 'DRILL' || p.phase === 'PROVE')
      .flatMap((p) => p.components)
      .flatMap((c) => (c.type === 'assessment_set' ? c.data?.questions || [] : [c.data]));
  }, [lesson]);

  /** Lessons in section order, so the list reads as three shorter lists. A
   *  section the package does not have simply does not appear. */
  const bySection = useMemo(() => {
    const out: Record<string, any[]> = {};
    for (const l of lessons) {
      // The lesson's own section, whatever the package calls it. Only a
      // lesson with no section at all is bucketed, and under a name that
      // says so rather than under another section's.
      (out[l.topic || 'Other'] ||= []).push(l);
    }
    return out;
  }, [lessons]);

  /** Which section the lesson list is showing. One at a time — thirty-eight
   *  cards is the thing students back away from, and stacking them under
   *  headings does not fix that: the list is still thirty-eight long. */
  const [section, setSection] = useState<string>(() => {
    try {
      return localStorage.getItem(`lomi_lesson_section:${pkg}`) || '';
    } catch {
      return '';
    }
  });

  const chooseSection = (sec: string) => {
    setSection(sec);
    try {
      localStorage.setItem(`lomi_lesson_section:${pkg}`, sec);
    } catch {
      /* storage unavailable — the filter still works for this visit */
    }
  };

  /** Sections this package actually has, in paper order. A remembered
   *  section that no longer exists falls back to the first. */
  // UAT's three first, in paper order, then anything else the package has, in
  // the order its lessons arrive — which is the order the API sorts them, i.e.
  // the package's own.
  const available = useMemo(() => {
    const have = Object.keys(bySection).filter((sec) => bySection[sec]?.length);
    const known = (UAT_SECTION_ORDER as readonly string[]).filter((sec) =>
      have.includes(sec)
    );
    return [...known, ...have.filter((sec) => !known.includes(sec))];
  }, [bySection]);
  const current = available.includes(section as any) ? section : available[0] || '';

  /**
   * The trial, stated.
   *
   * A student who has not paid gets the diagnostic, one lesson in every
   * section and 20 questions — a real amount of the product. None of that was
   * visible: locked lessons looked identical to open ones, so the only way to
   * discover the shape of the trial was to tap something and be refused.
   *
   * Shown only when something is actually locked, so it disappears the moment
   * a student pays without needing to know whether they have.
   */
  /**
   * The two knowledge checks, as two buttons.
   *
   * Free, and they stay free after purchase: the check is the hook, and a
   * student who has paid still needs to know where they stand. They are not
   * in `DiagnosticOnboarding` because that page lists a student's ESLCE
   * subjects — these are package content and belong in the package.
   *
   * `diagnosticAPI.start` and `/take-assessment` are the same path ESSLCE
   * uses; only the subject differs, because the checks ARE two pool subjects.
   */
  const [startingCheck, setStartingCheck] = useState<string | null>(null);
  const startCheck = async (subject: string, label: string) => {
    if (startingCheck) return;
    setStartingCheck(subject);
    try {
      track('package.check.start', { package: pkg, check: subject });
      const result = await diagnosticAPI.start(subject);
      navigate('/take-assessment', {
        state: {
          assessment: {
            id: result.assessment_id,
            title: `Knowledge Check — ${label}`,
            question_count: result.question_count,
          },
          isDiagnostic: true,
          isInitial: false,
          // Which package this check belongs to, carried explicitly.
          //
          // The assessment screens used to infer it from the title, which is a
          // display string — and this one uses an em dash the title-stripping
          // regex does not match, so every exit fell through to the ESLCE
          // dashboard: a student who took a UAT check and pressed Back landed
          // on a different exam. A key beats sniffing a label.
          packageKey: pkg,
        },
      });
    } catch (e) {
      setStartingCheck(null);
    }
  };

  /**
   * REMOVED from the Questions and Lessons rooms.
   *
   * It was put on both because Questions is the default landing room, and a
   * student who never opened Lessons would not learn the papers existed. That
   * reasoning predates the Mocks TAB, which is now one of three tabs at the
   * top of every screen in the package and does the same job without spending
   * the first screenful of the two rooms people actually came to use — and,
   * for a package with no authored papers yet, without leading both rooms
   * with an empty box.
   *
   * With both call sites gone the component has no callers, so it and its
   * stylesheet are deleted rather than left as a thing the next person has to
   * work out is dead. The Mocks room is `PackageMocks`.
   */

  const lockedCount = lessons.filter((l: any) => l.locked).length;
  // Styled to be SEEN. It was grey text on a grey card with a hairline border,
  // which is the visual language of a disclaimer — so the one message that
  // explains why most of the package is dimmed read as small print.
  const trialBanner = lockedCount > 0 && (
    <div className="ps-trial">
      <div className="ps-trial__main">
        <span className="ps-trial__badge">Free version</span>
        <p className="ps-trial__lead">
          You have <strong>two free mocks</strong>,{' '}
          <strong>20 practice questions</strong> and{' '}
          <strong>one lesson from each section</strong>.
        </p>
        <p className="ps-trial__sub">
          The other {lockedCount} lessons, every question with a worked solution
          and the full mock exam come with the package.
        </p>
      </div>
      <button
        className="ps-trial__cta"
        onClick={() => {
          track('package.trial.unlock', { package: pkg, shell });
          navigate(`/packages/${encodeURIComponent(pkg)}/checkout`);
        }}
      >
        Get full access — 400 birr
      </button>
    </div>
  );

  const openLesson = (l: any) => {
    // A locked lesson is an OFFER, not an error. Sending the student into the
    // lesson route so the server can refuse them with a 402 turns "this is part
    // of the full package" into "something went wrong".
    if (l.locked) {
      track('package.lesson.locked', { package: pkg, shell, slug: l.slug });
      navigate(`/packages/${encodeURIComponent(pkg)}/checkout`);
      return;
    }
    track('package.lesson.open', {
      package: pkg, shell, slug: l.slug, kind: l.kind, format: l.format,
    });
    navigate(`/p/${pkg}/${shell}/${l.slug}`);
  };

  /**
   * The free questions are gone, so the room they were in is gone.
   *
   * Disabling the filters INSIDE the room was not enough. A student who has
   * spent all twenty still walks into a question surface, reads a wall, and
   * has to work out that every chip above it is dead — and the section and
   * topic chips are only two of the ways in. The tab itself is the honest
   * boundary: one thing is closed, and it says so once.
   *
   * Scoped to the allowance the pool payload already carries, so a package
   * with full access and every ESSLCE subject — which never has an allowance
   * at all — is untouched.
   */
  /**
   * Spent, NOT empty. `remaining <= 0` is true in two very different
   * situations — the student used their free questions, or the package has
   * no questions yet — and this wall accuses them of the first. A package
   * being authored (EAU today) would have told every visitor "your 20 free
   * questions are used up" before a single question existed.
   *
   * `empty` comes from the server, which is the only side that knows whether
   * the free set is empty because there is no content.
   */
  const poolEmpty = !!pool.allowance?.empty;
  const trialSpent =
    !!pool.allowance &&
    pool.allowance.access !== 'full' &&
    !poolEmpty &&
    (pool.allowance.remaining ?? 1) <= 0;

  const switcher = useMemo(
    () => (
      <nav className="ps-seg" aria-label="How to study" role="tablist">
        {Object.entries(SHELLS)
          /* Three tabs, mock included, for every package.
             *
             * Production ships two, having filtered `mock` out while the papers
             * had no home worth navigating to. They have one now, and the mock
             * is the thing a student sets aside an hour to go and do — so it
             * earns a permanent slot rather than appearing and disappearing
             * with the content, which would make the room's shape depend on
             * how far authoring had got.
             *
             * The cost is that a package without papers has a tab that opens
             * an empty room, so that room has to say why it is empty rather
             * than render nothing. See the mock shell below. */
          /* Three tabs. The mock was held out of the bar while it had no home
             worth navigating to — it opened straight into a runner for a paper
             drawn at random from the practice pool. It now has a room: four
             authored papers, their status, and the free quick check that used
             to sit above the lesson grid pretending to be a mock. That is a
             destination, so it gets a tab. */
          .map(([k, s]) => {
          const locked = k === 'questions' && trialSpent;
          return (
          <button
            key={k}
            className={`ps-segbtn${k === shell ? ' on' : ''}${
              locked ? ' ps-segbtn--locked' : ''
            }`}
            role="tab"
            aria-selected={k === shell}
            onClick={() => {
              // A locked tab is an OFFER, not a dead button — the same answer
              // a locked lesson gives.
              if (locked) {
                track('package.shell.locked', { package: pkg, shell: k });
                navigate(`/packages/${encodeURIComponent(pkg)}/checkout`);
                return;
              }
              track('package.shell.switch', { package: pkg, from: shell, to: k });
              // A tab goes to the TAB, not to whatever was open inside the
              // last one. Carrying the slug across meant a student who opened
              // Age Problems then tapped "Questions" and back landed in Age
              // Problems again, and never saw the list.
              navigate(`/p/${pkg}/${k}`, { replace: true });
            }}
          >
            <span aria-hidden="true">{locked ? '🔒' : s.icon}</span> {s.name}
          </button>
          );
        })}
      </nav>
    ),
    [shell, pkg, slug, navigate, trialSpent]
  );

  /** Every page in the package wears the same shell. */
  const shellWrap = (children: React.ReactNode, cls = '') => (
    <>
      <PackageBar pkg={pkg} pkgName={pkgName} />
      <div className={`ps-page${cls}`}>{children}</div>
    </>
  );

  /**
   * The header for a room.
   *
   * It used to be four layers — title, blurb, tabs, stats — where the ESSLCE
   * equivalent is two: subject, then a segmented control. The title merely
   * repeated the active tab ("Lessons" above a Lessons tab) and the blurb was
   * a sentence nobody reads twice. Both are gone; the segment identifies the
   * room, which is its job.
   *
   * `title` is still taken for the lesson page, where the title is the lesson
   * and carries real information.
   */
  const header = (title?: string, sub?: string) => (
    <header className="ps-head">
      {title && <h1 className="ps-title">{title}</h1>}
      {sub && <p className="ps-muted">{sub}</p>}
      {switcher}
      {/* One number, and only because it is a CALL TO ACTION.
          "44 answered · 36% correct" were lifetime totals across the package.
          ESSLCE has no equivalent — it shows progress per module card and
          keeps streaks and XP on the dashboard — and a standing "36% correct"
          above every room is discouraging without being actionable: there is
          nothing a student can do about a lifetime average.

          Misses due is different. It is a queue with an end, it has an ESSLCE
          equivalent in Master Your Misses, and tapping it does something. So
          it survives, as a line rather than a panel — the three-figure card
          was costing a fifth of a phone screen on every room. */}
      {prog && prog.misses_due > 0 && (
        <button
          className="ps-review"
          onClick={() => navigate(`/p/${pkg}/questions`)}
        >
          <span className="ps-reviewn">{prog.misses_due}</span>
          <span>to review</span>
          <span className="ps-reviewgo" aria-hidden="true">→</span>
        </button>
      )}
    </header>
  );

  if (loading)
    return shellWrap(<div className="ps-spin" />, ' ps-centre');

  if (err)
    return shellWrap(
      <>
        <p className="ps-muted">{err}</p>
        <button className="ps-btn ghost" onClick={() => navigate('/packages')}>
          Back to packages
        </button>
      </>,
      ' ps-centre'
    );

  // Shell Q reuses PastESSLCEQuiz rather than reimplementing it: that
  // component already does filters, unseen-first selection, per-miss
  // explanations and — the part that matters — records through
  // pastExamAPI.startSession/saveAnswer, which is what feeds spaced review,
  // combos, rank and the evening push. A fresh implementation would look
  // identical and silently detach all four.
  // The mock room, and the runner one level below it.
  //
  //   /p/<pkg>/mock             the room — which paper, and how you did
  //   /p/<pkg>/mock/<FORM_CODE> that paper, under its clock
  //
  // The room is a real URL rather than a piece of runner state so that leaving
  // a paper lands back on the list, the back button does the obvious thing,
  // and a link to one paper can be sent.
  //
  // The runner reuses QuizCanvas — the same question surface as the aptitude
  // drill, Daily Focus and Weekly Mock — in its `examMode`, so the only thing
  // written from scratch is the behaviour no existing runner has.
  if (shell === 'mock') {
    if (!slug)
      return shellWrap(
        <>
          {header()}
          <PackageMocks
            pkg={pkg}
            onOpen={(form) => navigate(`/p/${pkg}/mock/${encodeURIComponent(form)}`)}
            onQuickCheck={startCheck}
            quickCheckBusy={startingCheck}
          />
        </>
      );

    return shellWrap(
      <>
        {!inSitting && header()}
        <PackageSitting
          pkg={pkg}
          // Which authored paper. Absent on the pool-sampled quick check,
          // which is the same runner with a different source.
          form={slug}
          onAskLomi={openAsk}
          onRunning={setInSitting}
          onLeave={() => {
            setInSitting(false);
            // Back to the room, not out of the package — a student who has
            // just finished one paper is deciding about the next one.
            navigate(`/p/${pkg}/mock`);
          }}
        />
        {/* The sitting hands the whole item up through `onAskLomi(it)`, and
            every identifier on it used to be dropped here: `questionText` and
            `contentTitle` are DISPLAY props, and the request is built from
            moduleId/componentId/questionId only. So Ask Lomi during a mock sent
            no ids at all, resolved no sources, and answered "Please share the
            question and I'll identify the topic for you!" — while the page was
            showing the student that very question.

            `esslce` rather than `quiz` because these are real question rows;
            it maps to context_type `eslce_quiz`, which is what makes the router
            resolve the id and cite it. */}
        <AIHelpPanel
          source="web_past_exam"
          context="esslce"
          componentType="practice_mcq"
          questionId={askQ?.question_id}
          questionText={askQ?.question}
          contentTitle="Full Mock Exam"
          isOpen={ask}
          onToggle={() => setAsk((v) => !v)}
          initialRequest={askInitial}
        />
      </>,
      inSitting ? ' ps-page--exam' : ''
    );
  }

  if (shell === 'questions') {
    // The tab is locked, but a URL is not a tab: /p/<pkg>/questions is
    // bookmarked, linked from notifications and reached by the back button.
    // The room has to close, not just the way in.
    // Nothing here yet — say so, and do not ask for money for it.
    if (poolEmpty) {
      return shellWrap(
        <>
          {header()}
          <div className="ps-empty">
            <h3>No questions yet</h3>
            <p className="ps-muted">
              We're still writing this package's questions. Nothing has been
              held back from you — check back soon.
            </p>
          </div>
        </>
      );
    }

    if (trialSpent) {
      return shellWrap(
        <>
          {header()}
          <div className="ps-empty">
            <h3>Your {pool.allowance?.limit ?? 20} free questions are used up</h3>
            <p className="ps-muted">
              The full package opens every question, all {lessons.length} lessons
              and the mock exam — and every answer keeps counting towards your
              misses, combos and rank.
            </p>
            <button
              className="ps-btn"
              onClick={() => {
                track('package.questions.wall.unlock', { package: pkg });
                navigate(`/packages/${encodeURIComponent(pkg)}/checkout`);
              }}
            >
              Get full access — 400 birr
            </button>
            {/* The lessons tab still has open lessons in it, so the student is
                sent somewhere real rather than left on the wall. */}
            <button
              className="ps-btn ghost"
              onClick={() => navigate(`/p/${pkg}/lessons`)}
            >
              Back to lessons
            </button>
          </div>
        </>
      );
    }
    const subs = pool.subjects;
    const active = subject || (subs.length === 1 ? subs[0].subject : null);
    const activeCount = subs.find((x) => x.subject === active)?.questions ?? 0;

    return shellWrap(
      <>
        {header()}
        {subs.length > 1 && (
          <div className="ps-subjects">
            {subs.map((x) => (
              <button
                key={x.subject}
                className={`ps-subject${x.subject === active ? ' on' : ''}`}
                onClick={() => {
                  setSubject(x.subject);
                  track('package.practice.subject', { package: pkg, subject: x.subject });
                }}
              >
                <span className="ps-subjname">{x.subject}</span>
                <span className="ps-subjcount">{x.questions}</span>
              </button>
            ))}
          </div>
        )}

        {active && activeCount > 0 ? (
          <PastESSLCEQuiz
            subject={active}
            // No ESLCE badge on authored UAT items — they are practice, not
            // past papers, and the badge would claim provenance they lack.
            // Every package room is authored practice, never an ESLCE paper.
            // Naming one package here left EAU on the `undefined` branch,
            // whose fallback stamps "ESLCE '<year> EC" — and a pool carries
            // year 0 by design, so EAU items were badged "ESLCE '0 EC": a
            // provenance claim that is both false and nonsense.
            // Open: EAU marks 34 items Past Question, and that per-item badge
            // is not plumbed to the client yet, so they are under-labelled
            // here rather than mislabelled.
            sourceLabel={'Practice'}
          />
        ) : (
          <div className="ps-empty">
            <h3>No questions in {active || 'this package'} yet</h3>
            <p className="ps-muted">
              The question pipeline for this package hasn&rsquo;t produced items yet.
              When it does they appear here, and every answer counts towards your
              misses, combos and rank.
            </p>
            <p className="ps-muted ps-small">
              Questions from other subjects are never shown here. If items are
              shared between packages they are copied in and re-tagged first, so
              what you practise is always the exam you are sitting.
            </p>
            <button className="ps-btn ghost" onClick={() => navigate(`/p/${pkg}/lessons`)}>
              Open the lesson
            </button>
          </div>
        )}
      </>
    );
  }

  if (!slug)
    return shellWrap(
      <>
        {header()}
        {trialBanner}
        {/* The ESSLCE module-card grid, reused rather than reinvented: a
            student moving between packages should meet one product, and this
            card already carries what they read — title, what it covers, how
            long, how much practice sits inside it. */}
        {/* Grouped by section. Thirty-eight cards in one undifferentiated
            list is the thing students back away from — and the paper itself
            is not one list, it is three parts. Ordered the way the paper runs
            and the way revision is weighted, not alphabetically. */}
        {/* One section at a time. The paper has three parts; showing all
            thirty-eight lessons at once is the wall a student turns away
            from, and grouping them under headings left the list exactly as
            long. Selecting a section replaces the list rather than scrolling
            further down it. */}
        {available.length > 1 && (
          <div className="ps-sectbar" role="tablist" aria-label="Section">
            {available.map((sec) => (
              <button
                key={sec}
                role="tab"
                aria-selected={sec === current}
                className={`ps-sectbtn${sec === current ? ' on' : ''}`}
                onClick={() => chooseSection(sec)}
              >
                {sectionLabel(sec)}
                <span className="ps-sectcount">{bySection[sec].length}</span>
              </button>
            ))}
          </div>
        )}
        <div className="kp-grid">
          {(bySection[current] || []).map((l) => (
            <div
              key={l.slug}
              className={`kp-mod kp-mod--${l.kind} kp-mod--click${
                l.locked ? ' kp-mod--locked' : ''
              }${
                l.student_progress?.status === 'completed'
                  ? ' kp-mod--done'
                  : ''
              }`}
              role="button"
              tabIndex={0}
              onClick={() => openLesson(l)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openLesson(l);
                }
              }}
            >
              <div className="kp-modhead">
                <h3>
                  {l.locked && <span className="kp-lock" aria-label="Locked">🔒</span>}
                  {!l.locked && l.student_progress?.status === 'completed' && (
                    <span className="kp-done" aria-label="Completed">✓</span>
                  )}
                  {l.title}
                </h3>
                {/* Only where the SECTION is itself the kind. UAT's two
                    sections are the two halves of the AAU test, so the badge
                    names something real there. EAU has five sections and
                    every lesson carries kind "quantitative" for layout, which
                    stamped "Quantitative" on Physics, Mechanical and Math
                    cards alike — a badge that never varies is furniture. */}
                {/verbal|quantitative/i.test(l.topic || '') && l.kind && (
                  <span className={`kp-difficulty kp-difficulty--${l.kind}`}>
                    {l.kind === 'verbal' ? 'Verbal' : 'Quantitative'}
                  </span>
                )}
              </div>

              {/* One inline row, the way the ESSLCE module card does it. The
                  same facts stacked over three rows made every card twice as
                  tall for no extra information. */}
              <div className="kp-modmeta">
                <span className="kp-subjtopic">
                  {/* "Quantitative Reasoning • Age Problems" put the SECTION
                      first and the topic second, so every card led with the
                      same three words. Read it the way a student would say
                      it: the exam, the half of the paper, then the topic. */}
                  {[
                    // Was the literal 'UAT', so every EAU lesson card read
                    // "UAT • Math • …". The package already knows its own
                    // name — there is no reason to spell one in here.
                    pkgName,
                    (l.topic || '').replace(/\s*Reasoning$/, ''),
                    l.subtopic,
                  ]
                    .filter(Boolean)
                    .join(' • ')}
                </span>
                <span>⏱️ {l.minutes} min</span>
                <span>📚 {l.components} parts</span>
              </div>

              {/* How far through. The list carried no progress at all, so a
                  student who had finished six lessons saw the same cards as one
                  who had finished none — while the identical ESLCE module card
                  two taps away showed both a bar and a percentage. Drawn only
                  once there is something to show, so an untouched list stays
                  quiet. */}
              {!l.locked &&
                l.student_progress &&
                l.student_progress.status !== 'not_started' && (
                  <div
                    className={`kp-prog${
                      l.student_progress.status === 'completed'
                        ? ' kp-prog--done'
                        : ''
                    }`}
                  >
                    <div className="kp-prog__bar">
                      <div
                        className="kp-prog__fill"
                        style={{
                          width: `${Math.min(
                            100,
                            l.student_progress.completion_percentage || 0
                          )}%`,
                        }}
                      />
                    </div>
                    <span className="kp-prog__text">
                      {l.student_progress.status === 'completed'
                        ? 'Completed'
                        : `${Math.round(
                            l.student_progress.completion_percentage || 0
                          )}%`}
                    </span>
                  </div>
                )}

              <div className="kp-modfoot">
                {/* The whole card is the target; this stays as the affordance
                    that says so. `pointerEvents: none` keeps the click on the
                    card so the handler fires once, not twice. */}
                <span className="kp-modbtn" style={{ pointerEvents: 'none' }}>
                  {/* A locked card that says "Start" promises something it does
                      not do. Tapping it opens the offer, so it says so. */}
                  {l.locked
                    ? 'Unlock →'
                    : l.student_progress?.status === 'completed'
                    ? 'Review →'
                    : l.student_progress?.status === 'in_progress'
                    ? 'Continue →'
                    : 'Start →'}
                </span>
              </div>
            </div>
          ))}
        </div>
        {lessons.length === 0 && <p className="ps-muted">No lessons published yet.</p>}
      </>
    );

  if (!lesson) return null;

  // Which body is about to render. The shared player brings its own Ask Lomi
  // panel; the other layouts do not, so the page-level panel and chip belong
  // to them alone. Mirrors the branch order below exactly — if that changes,
  // this must too.
  const usesSharedPlayer =
    !(lesson.format === 'claims' && !quiz) && !quiz && shell !== 'b' && shell !== 'c';

  return shellWrap(
    <>
      {header(lesson.title, undefined)}
      {/* The topic used to have its own row, which on a phone read
          "VERBAL REASONING · VERBAL" — one line of height to say "verbal"
          twice. It sits with the actions now.
          The kind pill that sat beside it is gone. It was already suppressed
          for every UAT lesson (the guard dropped it when the topic implied
          the kind, and UAT's two sections ARE "Quantitative Reasoning" and
          "Verbal Reasoning", so it always did) — the only package it ever
          rendered for was EAU, where it printed "QUANTITATIVE" next to
          "Physics". `kind` is a LAYOUT axis every package must set; it is not
          a fact about EAU worth showing a student. */}
      <div className="ps-tools">
        <span className="ps-kicker ps-kicker--inline">{lesson.topic}</span>
        {/* The Full/Brief toggle is gone from the phased lessons. It existed to
            choose between two documents; there is now one, and each card
            carries its own "Explain more" with the full prose a click away per
            section. A global density switch on top of per-card expansion is
            two controls for one idea. Claims still uses it — that format's
            rows genuinely open and close together. */}
        {lesson.format === 'claims' && (
          <div className="ps-density" role="group" aria-label="Reading density">
            <button className={brief ? '' : 'on'} onClick={() => setDensity(false)}>
              Expand all
            </button>
            <button className={brief ? 'on' : ''} onClick={() => setDensity(true)}>
              Collapse all
            </button>
          </div>
        )}
        {/* The quiz chip is gone from the header. A lesson already ENDS in
            its quiz — the phase strip walks Key Terms, Practice, Quiz — so a
            floating chip offering a second way in sat alone on the right,
            duplicating a section the student reaches by finishing. The quiz
            state and the shared player are untouched; only the shortcut is
            removed. */}
        {!usesSharedPlayer && (
          <button className="ps-chip" onClick={() => openAsk()}>
            💬 Ask Lomi
          </button>
        )}
      </div>

      {lesson.format === 'claims' && !quiz ? (
        <ClaimsBody
          lesson={lesson}
          brief={brief}
          comps={lesson.phases.flatMap((p) => p.components)}
          onAskLomi={openAsk}
          onSimilar={askSimilar}
          onRecord={recordAnswer}
        />
      ) : quiz ? (
        <GuidedQuiz
          items={quizItems}
          onAskLomi={openAsk}
          onSimilar={askSimilar}
          onRecord={recordAnswer}
          onDone={() => {
            track('package.quiz.complete', {
              package: pkg, shell, items: quizItems.length,
            });
            setQuiz(false);
          }}
        />
      ) : shell === 'b' ? (
        // Kept reachable by URL only — not in the nav. The sequencing these two
        // encode moves to notifications; the layouts stay so the comparison
        // that produced that conclusion can still be re-run.
        <ShellTimed lesson={lesson} brief={brief} onAskLomi={openAsk} onSimilar={askSimilar} onRecord={recordAnswer} subject={subject || undefined} onAskWith={askWith} />
      ) : shell === 'c' ? (
        <ShellDaily lesson={lesson} brief={brief} onAskLomi={openAsk} onSimilar={askSimilar} onRecord={recordAnswer} subject={subject || undefined} onAskWith={askWith} />
      ) : (
        /* The SHARED player, not a fork of it.
           There was a UAT-only player here. The reasoning for it was that
           MicroLearningModule serves 26k students mid-season and a UAT change
           must not be able to reach it — which was right about the risk and
           wrong about the remedy. The fork had to re-earn everything the
           original already did, and it got three of them wrong at once: quiz
           components rendered blank, Continue on a question was a button the
           server refused, and answering posted a payload that scored zero.
           The shared player has none of those bugs, and it renders every
           component type UAT publishes.
           Nothing here is UAT-specific: the player takes a module id and the
           API hands it content in the shape it already reads. */
        <MicroLearningModule
          studentId={localStorage.getItem('student_id') || ''}
          subject={subject || 'UAT'}
          moduleId={lesson.module_id}
          onExit={() => navigate(`/p/${pkg}/${shell}`)}
          onModuleComplete={() => {
            track('package.module.complete', { package: pkg, module: lesson.module_id });
            navigate(`/p/${pkg}/${shell}`);
          }}
        />
      )}

      {/* ONE Ask Lomi on a screen, never two.
          `MicroLearningModule` mounts its own AIHelpPanel, so when the shared
          player is what is rendering, this second panel put a duplicate
          launcher directly above the first — two identical buttons stacked at
          the foot of the lesson. The page-level panel is still needed for the
          layouts that do NOT use the player (claims, the guided quiz, the two
          URL-only shells), which have no panel of their own. */}
      {!usesSharedPlayer && (
        <AIHelpPanel
          source="web_lesson"
          context={askQ ? 'quiz' : 'content'}
          componentType={askQ ? 'practice_mcq' : 'lesson_content'}
          moduleId={lesson.module_id}
          questionText={askQ?.question}
          contentTitle={lesson.title}
          isOpen={ask}
          onToggle={() => setAsk((v) => !v)}
          initialRequest={askInitial}
        />
      )}
      {/* Leaving a lesson is the most-used control on the page and it was the
          faintest thing on it. */}
      <div className="ps-foot">
        <button className="ps-btn" onClick={() => navigate(`/p/${pkg}/${shell}`)}>
          ← Back to lessons
        </button>
      </div>
    </>
  );
};

export default PackageShell;
